import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import {
  Bug, Lightbulb, ThumbsUp, HelpCircle, Loader2, ExternalLink, ChevronDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  bug:         { label: 'Bug',        icon: Bug,        pill: 'bg-red-100 text-red-800 border-red-300' },
  suggestion:  { label: 'Idea',       icon: Lightbulb,  pill: 'bg-amber-100 text-amber-800 border-amber-300' },
  compliment:  { label: 'Compliment', icon: ThumbsUp,   pill: 'bg-green-100 text-green-800 border-green-300' },
  question:    { label: 'Question',   icon: HelpCircle, pill: 'bg-blue-100 text-blue-800 border-blue-300' },
};

const STATUS_OPTIONS = ['new', 'triaged', 'in_progress', 'resolved', 'dismissed'];

const fmtRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const FeedbackPage = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('new');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kindFilter) params.set('kind', kindFilter);
      if (statusFilter) params.set('status_filter', statusFilter);
      params.set('limit', '200');
      const res = await axios.get(`${API}/feedback?${params}`, { withCredentials: true });
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch {
      setItems([]); setCounts({});
    } finally {
      setLoading(false);
    }
  }, [kindFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, next) => {
    try {
      await axios.patch(`${API}/feedback/${id}`, { status: next }, { withCredentials: true });
      load();
    } catch (e) { /* eslint-disable-next-line no-alert */ alert(e.response?.data?.detail || 'Update failed'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="feedback-admin-page">
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Feedback Inbox</h1>
          <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>What testers and prospects are telling us about the product.</p>
        </header>

        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <FilterChip active={!kindFilter} onClick={() => setKindFilter('')} label="All" count={Object.values(counts).reduce((a, b) => a + b, 0)} />
          {Object.entries(KIND_META).map(([k, meta]) => (
            <FilterChip
              key={k}
              active={kindFilter === k}
              onClick={() => setKindFilter(kindFilter === k ? '' : k)}
              label={meta.label}
              count={counts[k] || 0}
              icon={meta.icon}
            />
          ))}
          <span className="text-xs text-slate-400 mx-2">·</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="feedback-status-filter"
            className="text-xs font-semibold border border-slate-300 rounded px-2 py-1.5 bg-white"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-md p-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Nothing matching this filter — yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="feedback-list">
            {items.map((it) => <FeedbackRow key={it.id} item={it} onStatus={setStatus} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const FilterChip = ({ active, onClick, label, count, icon: Icon }) => (
  <button
    onClick={onClick}
    data-testid={`feedback-filter-${label.toLowerCase()}`}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors border ${active ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`}
  >
    {Icon && <Icon className="w-3 h-3" strokeWidth={2.25} />}
    {label}
    {count > 0 ? (
      <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/30' : 'bg-slate-100 text-slate-700'}`}>{count}</span>
    ) : (
      <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/20 text-white/70' : 'bg-slate-50 text-slate-400'}`}>0</span>
    )}
  </button>
);

const FeedbackRow = ({ item, onStatus }) => {
  const [expand, setExpand] = useState(false);
  const meta = KIND_META[item.kind] || KIND_META.suggestion;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`feedback-row-${item.id}`}
      className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors"
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center border ${meta.pill} flex-shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.pill}`}>{meta.label}</span>
            {item.severity && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">SEV · {item.severity}</span>
            )}
            {item.is_demo_session && (
              <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">DEMO</span>
            )}
            <span className="text-xs text-slate-400">{fmtRelative(item.created_at)}</span>
            <select
              value={item.status}
              onChange={(e) => onStatus(item.id, e.target.value)}
              data-testid={`feedback-status-${item.id}`}
              className="ml-auto text-[11px] font-semibold border border-slate-300 rounded px-1.5 py-0.5 bg-white capitalize"
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
          <p className={`text-sm text-slate-800 whitespace-pre-wrap ${expand ? '' : 'line-clamp-3'}`}>{item.message}</p>
          {item.message.length > 200 && (
            <button onClick={() => setExpand(!expand)} className="text-[11px] text-[#002FA7] font-semibold mt-1 inline-flex items-center gap-1 hover:underline">
              {expand ? 'Show less' : 'Show more'} <ChevronDown className={`w-3 h-3 transition-transform ${expand ? 'rotate-180' : ''}`} />
            </button>
          )}
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
            {(item.user_name || item.user_email) && (
              <span>From: <span className="font-semibold text-slate-700">{item.user_name || item.user_email}</span>{item.user_role ? ` · ${item.user_role}` : ''}</span>
            )}
            {!item.user_name && !item.user_email && item.contact_email && (
              <span>Anonymous · contact: <span className="font-semibold text-slate-700">{item.contact_email}</span></span>
            )}
            {!item.user_name && !item.user_email && !item.contact_email && (
              <span className="italic text-slate-400">Anonymous</span>
            )}
            {item.page_url && (
              <span className="inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                <a href={item.page_url} className="hover:underline" target="_blank" rel="noreferrer">{item.page_url}</a>
              </span>
            )}
            {item.viewport && <span>{item.viewport}</span>}
            {item.console_error_count > 0 && (
              <span className="text-red-600">{item.console_error_count} console error{item.console_error_count !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
